import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import axios from '../config/api';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/format';
import { useToast } from '../context/ToastContext';
import GetHelpSection from '../components/GetHelpSection';
import DeleteAccountPanel from '../components/DeleteAccountPanel';
import {
  isCheckoutCancelUrl,
  openHostedCheckoutUrl,
  writeMpgsPayLaunch,
} from '../utils/mpgsCheckout';
import { cartPathFromCheckoutExit } from '../utils/mpgsCheckoutUrls';
import {
  SUBSCRIPTION_FEE_JOD,
  clearPendingSubscriptionCheckout,
  hasPendingSubscriptionCheckout,
  writePendingSubscriptionCheckout,
} from '../utils/mpgsSubscription';

function ProfileSection({
  id,
  title,
  summary,
  expanded,
  onToggle,
  children,
}: {
  id: string;
  title: string;
  summary?: string;
  expanded: boolean;
  onToggle: (id: string) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden min-w-0">
      <button
        type="button"
        id={`profile-section-${id}`}
        aria-expanded={expanded}
        aria-controls={`profile-panel-${id}`}
        onClick={() => onToggle(id)}
        className="w-full flex items-center justify-between gap-3 px-6 py-4 text-left hover:bg-amber-50/60 transition"
        style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.06)' }}
      >
        <span className="min-w-0 flex-1">
          <span className="block text-xl font-bold text-amber-800">{title}</span>
          {summary && !expanded && (
            <span className="block text-sm text-gray-500 mt-0.5 truncate">{summary}</span>
          )}
        </span>
        <span
          className={`shrink-0 text-amber-700 text-lg transition-transform ${expanded ? 'rotate-180' : ''}`}
          aria-hidden
        >
          ▼
        </span>
      </button>
      {expanded && (
        <div
          id={`profile-panel-${id}`}
          role="region"
          aria-labelledby={`profile-section-${id}`}
          className="px-6 pb-6 pt-0 border-t border-amber-100/80"
        >
          {children}
        </div>
      )}
    </div>
  );
}

export default function Profile() {
  const { user, updateUser } = useAuth();
  const { showToast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [pointsHistory, setPointsHistory] = useState<any[]>([]);
  const [walletHistory, setWalletHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [profileName, setProfileName] = useState('');
  const [profilePhone, setProfilePhone] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const subscriptionReturnHandled = useRef<string | null>(null);
  const SUBSCRIPTION_FEE = SUBSCRIPTION_FEE_JOD;

  const toggleSection = (id: string) => {
    setExpandedSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => {
    if (user) {
      setProfileName(user.name || '');
      setProfilePhone(user.phone || '');
      fetchPointsHistory();
      fetchWalletHistory();
    }
  }, [user]);

  const handleSaveAccountInfo = async () => {
    const cleanName = profileName.trim();
    const cleanPhone = profilePhone.trim();

    if (!cleanName) {
      showToast('Name cannot be empty.', 'error');
      return;
    }
    if (cleanPhone && (cleanPhone.length < 8 || cleanPhone.length > 24)) {
      showToast('Phone number must be between 8 and 24 characters.', 'error');
      return;
    }

    const payload: { name?: string; phone?: string | null } = {};
    if (cleanName !== (user?.name || '')) {
      payload.name = cleanName;
    }
    if (cleanPhone !== (user?.phone || '')) {
      payload.phone = cleanPhone || null;
    }
    if (Object.keys(payload).length === 0) {
      showToast('No account changes to save.', 'info');
      return;
    }

    setSavingProfile(true);
    try {
      await axios.put('/api/auth/me', payload);
      await updateUser();
      showToast('Account information updated.', 'success');
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Failed to update account information.', 'error');
    } finally {
      setSavingProfile(false);
    }
  };

  const fetchPointsHistory = async () => {
    try {
      const response = await axios.get('/api/points/history');
      setPointsHistory(response.data);
    } catch (error) {
      console.error('Error fetching points history:', error);
    }
  };

  const fetchWalletHistory = async () => {
    try {
      const response = await axios.get('/api/wallet/history');
      setWalletHistory(response.data || []);
    } catch (error) {
      console.error('Error fetching wallet history:', error);
    }
  };

  const activatePremium = useCallback(
    async (paymentIntentId: string) => {
      await axios.post('/api/subscriptions/user', {
        subscription_type: 'premium',
        payment_confirmed: true,
        payment_intent_id: paymentIntentId,
      });
      clearPendingSubscriptionCheckout();
      sessionStorage.removeItem('mpgsSuccessIndicator');
      sessionStorage.removeItem('mpgsGatewayOrderId');
      sessionStorage.removeItem('mpgsPayLaunch');
      await updateUser();
      setMessage('Subscription activated.');
      showToast(
        `Premium is on. Payment of ${formatCurrency(SUBSCRIPTION_FEE)} went through.`,
        'success'
      );
    },
    [showToast, updateUser]
  );

  const completeSubscriptionReturn = useCallback(async () => {
    const params = new URLSearchParams(location.search);
    if (params.get('mpgs_return') !== '1' || !hasPendingSubscriptionCheckout()) return;

    const gatewayOrderId = params.get('gateway_order_id') || params.get('order.id') || '';
    if (!gatewayOrderId) {
      showToast('Payment callback is missing subscription details.', 'error');
      return;
    }
    if (subscriptionReturnHandled.current === gatewayOrderId) return;
    subscriptionReturnHandled.current = gatewayOrderId;

    window.history.replaceState({}, '', '/profile');
    setExpandedSections((prev) => ({ ...prev, subscription: true }));
    setLoading(true);
    setMessage('');
    try {
      let confirm: { data?: { success?: boolean; error?: string; transactionId?: string } } | null =
        null;
      let lastError: unknown = null;
      for (let i = 0; i < 3; i += 1) {
        try {
          confirm = await axios.post('/api/payments/mpgs/confirm-order', { gatewayOrderId });
          lastError = null;
          break;
        } catch (err) {
          lastError = err;
          const msg = String((err as { response?: { data?: { error?: string } } })?.response?.data?.error || '')
            .toLowerCase();
          if (!msg.includes('not completed') || i === 2) break;
          await new Promise((resolve) => window.setTimeout(resolve, 1400));
        }
      }
      if (!confirm && lastError) throw lastError;
      if (!confirm?.data?.success) {
        throw new Error(confirm?.data?.error || 'Payment not completed');
      }
      await activatePremium(confirm.data.transactionId || `mpgs:${gatewayOrderId}`);
    } catch (error: any) {
      const errMsg =
        error?.response?.data?.error || error?.message || 'Failed to activate subscription.';
      setMessage(errMsg);
      showToast(errMsg, 'error');
    } finally {
      setLoading(false);
    }
  }, [activatePremium, location.search, showToast]);

  useEffect(() => {
    void completeSubscriptionReturn();
  }, [completeSubscriptionReturn]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('mpgs_cancel') !== '1') return;
    clearPendingSubscriptionCheckout();
    sessionStorage.removeItem('mpgsSuccessIndicator');
    sessionStorage.removeItem('mpgsGatewayOrderId');
    sessionStorage.removeItem('mpgsPayLaunch');
    window.history.replaceState({}, '', '/profile');
    setExpandedSections((prev) => ({ ...prev, subscription: true }));
    setLoading(false);
    showToast('Subscription payment was cancelled.', 'info');
  }, [location.search, showToast]);

  const handleSubscribe = async () => {
    if (!user || user.isGuest) {
      showToast('Sign in to subscribe to Premium.', 'info');
      navigate('/login');
      return;
    }
    if (user.subscription_active) return;

    setLoading(true);
    setMessage('');
    try {
      const randomPart =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID().replace(/-/g, '').slice(0, 12)
          : `${Date.now()}${Math.floor(Math.random() * 1000000)}`.slice(-12);
      const gatewayOrderId = `QH-SUB-${Date.now()}-${randomPart}`.slice(0, 40);
      const { data } = await axios.post(
        '/api/payments/mpgs/create-checkout',
        {
          amount: SUBSCRIPTION_FEE,
          currency: 'JOD',
          gatewayOrderId,
          orderReference: gatewayOrderId,
          returnBaseUrl: typeof window !== 'undefined' ? window.location.origin : undefined,
          description: 'QahwaHub Premium subscription (2.50 JOD / month)',
          paymentMethod: 'visa',
          checkoutMode: 'PAYMENT_LINK',
          customer: {
            email: user.email,
            firstName: user.name?.split(' ')?.[0] || 'Customer',
            lastName: user.name?.split(' ')?.slice(1).join(' ') || '',
            phone: user.phone || '',
          },
        },
        { timeout: 25000 }
      );
      if (!data?.checkoutUrl && !(data?.checkoutUrls || []).length) {
        throw new Error('NI did not return a payment page. Please try again.');
      }
      const effectiveGatewayOrderId = String(data.gatewayOrderId || gatewayOrderId);
      writePendingSubscriptionCheckout({ fee: SUBSCRIPTION_FEE, subscriptionType: 'premium' });
      sessionStorage.setItem('mpgsSuccessIndicator', String(data.successIndicator || ''));
      sessionStorage.setItem('mpgsGatewayOrderId', effectiveGatewayOrderId);
      const checkoutUrl =
        String(data.checkoutUrl || '').trim() ||
        (data.checkoutUrls || []).map((url: string) => String(url || '').trim()).find(Boolean) ||
        null;
      writeMpgsPayLaunch({
        sessionId: String(data.sessionId || ''),
        scriptUrls: data.checkoutScriptUrls || [],
        gatewayOrderId: effectiveGatewayOrderId,
        checkoutUrl,
      });
      if (!checkoutUrl) {
        throw new Error('NI did not return a payment page. Please try again.');
      }
      if (Capacitor.isNativePlatform()) {
        const result = await openHostedCheckoutUrl(checkoutUrl);
        if (result.status === 'cancel' || isCheckoutCancelUrl(result.url)) {
          clearPendingSubscriptionCheckout();
          showToast('Subscription payment was cancelled.', 'info');
          return;
        }
        navigate(cartPathFromCheckoutExit(result.url), { replace: true });
        return;
      }
      window.location.assign(checkoutUrl);
    } catch (error: any) {
      clearPendingSubscriptionCheckout();
      const errMsg =
        error?.response?.data?.error || error?.message || 'Could not start subscription payment.';
      setMessage(errMsg);
      showToast(errMsg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setLoading(true);
    setMessage('');
    try {
      await axios.delete('/api/subscriptions/user');
      setMessage('Subscription cancelled.');
      await updateUser();
    } catch (error: any) {
      setMessage(error.response?.data?.error || 'Failed to cancel subscription');
    } finally {
      setLoading(false);
    }
  };


  const pageBottomPad =
    'pb-[calc(5.5rem+env(safe-area-inset-bottom,0px))] lg:pb-8';

  if (!user) {
    return (
      <div className={`container mx-auto px-4 py-8 ${pageBottomPad}`}>Loading...</div>
    );
  }

  return (
    <div className={`container mx-auto px-4 py-8 ${pageBottomPad}`}>
      <h1 className="text-3xl font-bold mb-6 text-amber-800">Profile</h1>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ProfileSection
          id="account"
          title="Account Information"
          summary={user.name}
          expanded={!!expandedSections.account}
          onToggle={toggleSection}
        >
          <div className="space-y-3 pt-4">
            <div>
              <label className="text-gray-600 text-sm">Name</label>
              <input
                type="text"
                value={profileName}
                onChange={(e) => setProfileName(e.target.value)}
                className="w-full mt-1 px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-amber-500"
                maxLength={80}
              />
            </div>
            <div>
              <label className="text-gray-600 text-sm">Email</label>
              <p className="font-semibold">{user.email}</p>
            </div>
            <div>
              <label className="text-gray-600 text-sm">Phone</label>
              <input
                type="tel"
                value={profilePhone}
                onChange={(e) => setProfilePhone(e.target.value)}
                className="w-full mt-1 px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-amber-500"
                placeholder="e.g., +9627XXXXXXXX"
                maxLength={24}
              />
              <p className="text-xs text-gray-500 mt-1">
                Add a phone number so delivery can contact you when they arrive.
              </p>
            </div>
            <button
              type="button"
              onClick={handleSaveAccountInfo}
              disabled={savingProfile}
              className="bg-amber-700 text-white px-4 py-2 rounded hover:bg-amber-800 disabled:opacity-50 font-semibold"
              style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.1)', pointerEvents: savingProfile ? 'none' : 'auto' }}
            >
              {savingProfile ? 'Saving...' : 'Save Account Info'}
            </button>
          </div>
        </ProfileSection>

        <ProfileSection
          id="delete-account"
          title="Delete account"
          summary="Remove your personal details"
          expanded={!!expandedSections['delete-account']}
          onToggle={toggleSection}
        >
          <div className="pt-4">
            <DeleteAccountPanel />
          </div>
        </ProfileSection>

        <ProfileSection
          id="points"
          title="Points & Rewards"
          summary={`${user.points.toString().replace(/\.0+$/, '')} points`}
          expanded={!!expandedSections.points}
          onToggle={toggleSection}
        >
          <div className="pt-4">
            <div className="text-4xl font-bold text-amber-700 mb-2">{user.points.toString().replace(/\.0+$/, '')}</div>
            <p className="text-gray-600">Available Points</p>
            <p className="text-sm text-gray-500 mt-2">
              Earn 1 point per 1 JOD on drinks only. Redeem 50 points for 1 free coffee.
            </p>
          </div>
        </ProfileSection>

        <ProfileSection
          id="wallet"
          title="Qahwahub Credit"
          summary={formatCurrency(user.wallet_balance || 0)}
          expanded={!!expandedSections.wallet}
          onToggle={toggleSection}
        >
          <div className="pt-4">
            <div className="text-4xl font-bold text-indigo-700 mb-2">
              {formatCurrency(user.wallet_balance || 0)}
            </div>
            <p className="text-gray-600">Available wallet credit</p>
            <p className="text-sm text-gray-500 mt-2">
              Approved issue refunds to Qahwahub credit are added here and used at checkout.
            </p>
          </div>
        </ProfileSection>

        <ProfileSection
          id="subscription"
          title="Subscription"
          summary={user.subscription_active ? 'Premium active' : 'Not subscribed'}
          expanded={!!expandedSections.subscription}
          onToggle={toggleSection}
        >
          <div className="pt-4">
          {user.subscription_active ? (
            <div>
              <p className="text-green-600 font-semibold mb-4">
                ✓ You have an active Premium subscription
              </p>
              <p className="text-sm text-gray-600 mb-4">
                Benefits:
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>2× points on drinks</li>
                  <li>Lower service fee at checkout</li>
                  <li>Exclusive offers and notifications</li>
                </ul>
              </p>
                <button
                  type="button"
                  onClick={handleCancelSubscription}
                  disabled={loading}
                  className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50 font-semibold"
                  style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.1)', pointerEvents: loading ? 'none' : 'auto' }}
                >
                  Cancel Subscription
                </button>
            </div>
          ) : (
            <div>
              <p className="text-gray-600 mb-4">
                Upgrade to Premium for exclusive benefits:
              </p>
              <ul className="list-disc list-inside mb-4 text-sm text-gray-600 space-y-1">
                <li>2× points on drinks</li>
                <li>Lower service fee at checkout</li>
                <li>Exclusive offers and notifications</li>
              </ul>
              <div className="mb-4 p-3 bg-amber-50 rounded border border-amber-200">
                <p className="text-sm font-semibold text-amber-800 mb-1">Subscription Fee</p>
                <p className="text-2xl font-bold text-amber-700">{formatCurrency(SUBSCRIPTION_FEE)}<span className="text-base font-semibold text-amber-800"> / month</span></p>
                <p className="text-xs text-gray-600 mt-1">Monthly Premium subscription</p>
              </div>
              <button
                type="button"
                onClick={handleSubscribe}
                disabled={loading}
                className="w-full bg-amber-700 text-white px-4 py-2 rounded hover:bg-amber-800 disabled:opacity-50 font-semibold"
                style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.1)', pointerEvents: loading ? 'none' : 'auto' }}
              >
                {loading ? 'Processing...' : `Subscribe for ${formatCurrency(SUBSCRIPTION_FEE)} / month`}
              </button>
            </div>
          )}
          {message && (
            <div className={`mt-4 p-3 rounded ${
              message.includes('activated') || message.includes('cancelled')
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-700'
            }`}>
              {message}
            </div>
          )}
          </div>
        </ProfileSection>

        <ProfileSection
          id="help"
          title="Get Help"
          summary="Payments and contact support"
          expanded={!!expandedSections.help}
          onToggle={toggleSection}
        >
          <GetHelpSection />
        </ProfileSection>

        <ProfileSection
          id="wallet-history"
          title="Wallet History"
          summary={
            walletHistory.length === 0
              ? 'No wallet transactions yet'
              : `${walletHistory.length} transaction${walletHistory.length === 1 ? '' : 's'}`
          }
          expanded={!!expandedSections['wallet-history']}
          onToggle={toggleSection}
        >
          <div className="space-y-2 pt-4">
            {walletHistory.length === 0 ? (
              <p className="text-gray-500 text-sm">No wallet transactions yet</p>
            ) : (
              walletHistory.map((transaction) => (
                <div
                  key={transaction.id}
                  className="flex justify-between items-center p-2 bg-gray-50 rounded"
                >
                  <div className="flex-1">
                    <p className="text-sm font-semibold">{transaction.description}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(transaction.created_at).toLocaleString()}
                    </p>
                  </div>
                  <span
                    className={`font-bold ${
                      transaction.amount > 0 ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {transaction.amount > 0 ? '+' : ''}
                    {formatCurrency(transaction.amount)}
                  </span>
                </div>
              ))
            )}
          </div>
        </ProfileSection>

        <ProfileSection
          id="history"
          title="Points History"
          summary={
            pointsHistory.length === 0
              ? 'No transactions yet'
              : `${pointsHistory.length} transaction${pointsHistory.length === 1 ? '' : 's'}`
          }
          expanded={!!expandedSections.history}
          onToggle={toggleSection}
        >
          <div className="space-y-2 pt-4">
            {pointsHistory.length === 0 ? (
              <p className="text-gray-500 text-sm">No transactions yet</p>
            ) : (
              pointsHistory.map(transaction => (
                <div
                  key={transaction.id}
                  className="flex justify-between items-center p-2 bg-gray-50 rounded"
                >
                  <div className="flex-1">
                    <p className="text-sm font-semibold">{transaction.description}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(transaction.created_at).toLocaleString()}
                    </p>
                  </div>
                  <span
                    className={`font-bold ${
                      transaction.points > 0 ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {transaction.points > 0 ? '+' : ''}
                    {transaction.points.toString().replace(/\.0+$/, '')}
                  </span>
                </div>
              ))
            )}
          </div>
        </ProfileSection>
      </div>
    </div>
  );
}
